/**
 * A RovoLogo is a subset API that works for both @atlaskit/logo and @atlaskit/icon
 * We use one or the other depending on 1P or 3P and need a consistent API internall
 */
import React from 'react';

import type { Size as IconSize } from '@atlaskit/icon/types';

import { SVGIcon } from './svg-icon';
import type { CorrectableRovoSize, Size } from './types';

function isCorrectableRovoSize(size: string): size is CorrectableRovoSize {
	return ['xsmall', 'xxsmall', 'small', 'medium', 'large'].includes(size);
}

export type RovoLogoGlyphWrapper = (
	glyph: () => JSX.Element,
	defaultLabel?: string,
	defaultSize?: Size,
) => RovoLogo;
export type RovoLogoLogoWrapper = (LogoComponent: any, defaultLabel?: string) => RovoLogo;

export type RovoLogoProps = {
	size?: Size;
	label?: string;
	testId?: string;
};

export type RovoLogo = (props: RovoLogoProps) => JSX.Element;

/**
 * Due a bug in ADS custom icons are one size smaller (than atlaskit/logo)
 * To accommodate for this behavior you need to use one size bigger than what you need
 * See: https://atlassian.slack.com/archives/CFJ9DU39U/p1714096323217799
 */
const correctedSize: Record<CorrectableRovoSize, IconSize> = {
	xsmall: 'small',
	xxsmall: 'small',
	small: 'medium',
	medium: 'large',
	large: 'xlarge',
};

/**
 * A wrapper for rendering 3P svg illustrations in a similar way for atlaskit icon components
 *
 * @param {} glyph function returning an SVG as JSX
 * @param defaultLabel an accessible label for the logo
 */
export const RovoLogoFromSVG: RovoLogoGlyphWrapper = function RovoLogoFromSVG(
	glyph,
	defaultLabel = '',
	defaultSize?: Size,
) {
	return ({ label = defaultLabel, size = defaultSize, testId }) => (
		<SVGIcon
			label={label}
			glyph={glyph}
			size={size && isCorrectableRovoSize(size) ? correctedSize[size] : 'medium'}
			testId={testId}
		/>
	);
};

/**
 * A wrapper for providing an internal \@atlaskit/icon - alike experience for working with \@atlaskit/logo
 *
 * Internally uses <LogoComponent ... />
 *
 * @param {} LogoComponent an import from \@atlaskit/logo
 * @param defaultLabel an accessible label for the logo
 */
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const RovoLogoFromLogo: RovoLogoLogoWrapper = function RovoLogoFromLogo(
	LogoComponent,
	defaultLabel = '',
) {
	return ({ size, label, testId }) => {
		return (
			<LogoComponent
				label={label || defaultLabel}
				testId={testId}
				size={size || 'small'}
				appearance="brand"
			/>
		);
	};
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function getRovoLogoFromSVG(
	iconGlyph: () => JSX.Element,
	iconLabel: string,
	size?: Size,
): RovoLogo {
	return RovoLogoFromSVG(iconGlyph, iconLabel, size);
}
